import {
  Button,
  Card,
  Form,
  InputNumber,
  Space,
  Spin,
  TimePicker,
  Typography,
  message,
} from 'antd';
import { useRequest } from 'ahooks';
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { CommentIntervalConfig, systemController } from '../../../api';
import {
  DEFAULT_INTERVAL_SECONDS,
  IntervalRuleForm,
  TIME_FORMAT,
  validateIntervals,
} from './commentIntervalValidate';

interface CommentIntervalForm {
  intervals: IntervalRuleForm[];
}

export default function CommentIntervalCard() {
  const [form] = Form.useForm<CommentIntervalForm>();

  const { loading: getLoading, refresh } = useRequest(
    () => systemController.getCommentIntervalConfig(),
    {
      onSuccess: (data) => {
        const intervals = (data?.intervals || []).map((r) => ({
          start: r.start ? dayjs(r.start, TIME_FORMAT) : undefined,
          end: r.end ? dayjs(r.end, TIME_FORMAT) : undefined,
          seconds: r.seconds,
        }));
        form.setFieldsValue({ intervals });
      },
    },
  );

  const { loading: updateLoading, run: update } = useRequest(
    (dto: CommentIntervalConfig) => systemController.updateCommentIntervalConfig(dto),
    {
      manual: true,
      onSuccess: () => {
        message.success('更新成功');
        refresh();
      },
      onError: (e) => {
        message.error(e.message || '更新失败');
      },
    },
  );

  const handleSave = async () => {
    const values = await form.validateFields();
    const intervals = values.intervals || [];
    const error = validateIntervals(intervals);
    if (error) {
      message.error(error);
      return;
    }
    update({
      intervals: intervals.map((r) => ({
        start: r.start!.format(TIME_FORMAT),
        end: r.end!.format(TIME_FORMAT),
        seconds: r.seconds,
      })),
    });
  };

  return (
    <Card title="备注间隔限制">
      <Typography.Paragraph type="secondary">
        限制同一业务员在同一客户上连续添加跟进备注的最小间隔。按时段配置：当前时刻落在某时段内时使用该时段的间隔秒数；
        未配置任何时段、或不落在任何时段时，统一使用默认 {DEFAULT_INTERVAL_SECONDS} 秒。时段不允许跨天，跨午夜请拆成两段；各时段不能重合。
      </Typography.Paragraph>
      <Spin spinning={getLoading || updateLoading}>
        <Form form={form} layout="vertical" autoComplete="off">
          <Form.List name="intervals">
            {(fields, { add, remove }) => (
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {fields.map(({ key, name, ...rest }) => (
                  <Space key={key} align="baseline" wrap>
                    <Form.Item
                      {...rest}
                      name={[name, 'start']}
                      label="开始时间"
                      rules={[{ required: true, message: '请选择开始时间' }]}
                    >
                      <TimePicker format={TIME_FORMAT} minuteStep={1} placeholder="开始" />
                    </Form.Item>
                    <Form.Item
                      {...rest}
                      name={[name, 'end']}
                      label="结束时间"
                      rules={[{ required: true, message: '请选择结束时间' }]}
                    >
                      <TimePicker format={TIME_FORMAT} minuteStep={1} placeholder="结束" />
                    </Form.Item>
                    <Form.Item
                      {...rest}
                      name={[name, 'seconds']}
                      label="间隔（秒）"
                      rules={[
                        { required: true, message: '请填写间隔秒数' },
                        { type: 'integer', min: 1, max: 86400, message: '必须为 1-86400 的整数' },
                      ]}
                    >
                      <InputNumber min={1} max={86400} precision={0} placeholder="秒" />
                    </Form.Item>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => remove(name)}
                    >
                      删除
                    </Button>
                  </Space>
                ))}
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()} block>
                  新增时段
                </Button>
              </Space>
            )}
          </Form.List>
          <Space style={{ marginTop: 16 }}>
            <Button type="primary" onClick={handleSave} loading={updateLoading}>
              保存
            </Button>
            <Button icon={<ReloadOutlined />} onClick={refresh}>
              刷新
            </Button>
          </Space>
        </Form>
      </Spin>
    </Card>
  );
}
